import { TError, TProfile } from '@onesource/schemas';
import { useQuery } from '@tanstack/react-query';

import axios, { setTokenToAxios } from '../axios';
import { ENTITY } from './entity';

export const getUsersQueryKey = () => {
  return [ENTITY];
};

export const queryFn = async (): Promise<TProfile> => {
  // The auth-check endpoint resolves the central profile. In multi-workspace
  // apps (dashboard) the global axios baseURL follows the selected workspace,
  // and some workspace APIs can't resolve the central profile (e.g.
  // notification-api 500s, ryc-api 401s for non-RYC users) — which broke login
  // for those workspaces. Pin auth-check to the members auth hub when the app
  // defines VITE_MEMBERS_API_ENDPOINT. Apps that don't (fit-fe, ryc-fe,
  // addon-fe) keep hitting their own API, which serves auth-check fine.
  const membersBaseURL = import.meta.env.VITE_MEMBERS_API_ENDPOINT;
  const config = membersBaseURL ? { baseURL: membersBaseURL } : undefined;

  const response = await axios.get<TProfile>('/auth/auth-check', config);
  return response.data;
};

type TParams = {
  token?: string | null;
};

export const useGetAllProfileQuery = (params?: TParams) => {
  const token = params?.token;
  const isEnabled = !!token?.length;

  if (token) {
    setTokenToAxios(token);
  }

  return useQuery<TProfile, TError>({
    queryKey: getUsersQueryKey(),
    queryFn,
    enabled: isEnabled,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: (failureCount, error) => {
      // Don't retry on auth errors — token is invalid, retrying won't help
      const status = (error as { response?: { status?: number } })?.response?.status;

      if (status && status >= 400 && status < 500) {
        return false;
      }

      return failureCount < 5;
    },
    retryDelay: 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: 3 * 60 * 60 * 1000, // 3 hours
  });
};

export type TUseGetProfileQueryResult = ReturnType<typeof useGetAllProfileQuery>;
